import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

/**
 * UpdateNotification — bottom-right toast for the desktop auto-update flow.
 *
 * Two design choices that look incidental but solve real bugs:
 *
 * 1. **Rehydrate from main on mount.** electron-updater fires
 *    `update-available` exactly once per check. If the event arrives while
 *    the renderer is loading (very common — first launch sees an update
 *    within a few hundred ms), or if the component remounts for any
 *    reason (in v1.0.10 the toast vanished when navigating away from
 *    /welcome), the in-memory React state is gone and there's no way to
 *    get it back without another check. We now ask main for its cached
 *    `lastUpdateState` on mount and replay it locally.
 *
 * 2. **Render via createPortal(document.body).** Some routes wrap pages
 *    in containers that have CSS transforms / filters / will-change set,
 *    which traps `position: fixed` children inside them. Portalling to
 *    body sidesteps the entire question — the toast is positioned
 *    relative to the viewport regardless of which route is rendering
 *    underneath it.
 */
export default function UpdateNotification() {
  const [state, setState] = useState('hidden'); // hidden | available | downloading | ready
  const [info, setInfo] = useState(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!window.electronAPI) return;

    // Rehydrate from main's cached state. If a check already found an
    // update before this component mounted, we'd otherwise never know.
    let cancelled = false;
    if (typeof window.electronAPI.getUpdateState === 'function') {
      window.electronAPI.getUpdateState().then((cached) => {
        if (cancelled || !cached || cached.kind === 'idle') return;
        if (cached.info) setInfo(cached.info);
        if (cached.kind === 'available') setState('available');
        else if (cached.kind === 'downloading') {
          setProgress(cached.progress || 0);
          setState('downloading');
        } else if (cached.kind === 'ready') {
          setState('ready');
        }
      }).catch(() => { /* non-fatal; the listeners below still work */ });
    }

    const cleanups = [
      window.electronAPI.onUpdateAvailable((updateInfo) => {
        setInfo(updateInfo);
        setState('available');
      }),
      window.electronAPI.onUpdateProgress((p) => {
        setProgress(Math.round(p.percent));
        setState('downloading');
      }),
      window.electronAPI.onUpdateDownloaded(() => {
        setState('ready');
      }),
    ];

    return () => {
      cancelled = true;
      cleanups.forEach((fn) => fn());
    };
  }, []);

  if (state === 'hidden') return null;

  // Portal to body so parent CSS (transform/filter/will-change/etc.) can't
  // trap the fixed-positioned toast inside an offscreen subtree.
  const toast = (
    <div style={{
      position: 'fixed',
      bottom: 16,
      right: 16,
      background: '#1e293b',
      color: '#f1f5f9',
      borderRadius: 8,
      padding: '12px 16px',
      boxShadow: '0 4px 24px rgba(0,0,0,0.3)',
      zIndex: 9999,
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      fontSize: 14,
      maxWidth: 360,
    }}>
      {state === 'available' && (
        <>
          <span>Update {info?.version} available</span>
          <button
            onClick={() => window.electronAPI.downloadUpdate()}
            style={{
              background: '#4F46E5',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              padding: '6px 12px',
              cursor: 'pointer',
              fontSize: 13,
              whiteSpace: 'nowrap',
            }}
          >
            Download
          </button>
          <button
            onClick={() => setState('hidden')}
            style={{
              background: 'transparent',
              color: '#94a3b8',
              border: 'none',
              cursor: 'pointer',
              fontSize: 16,
              padding: '0 4px',
            }}
          >
            &times;
          </button>
        </>
      )}

      {state === 'downloading' && (
        <>
          <span>Downloading update... {progress}%</span>
          <div style={{
            flex: 1,
            height: 4,
            background: '#334155',
            borderRadius: 2,
            overflow: 'hidden',
            minWidth: 80,
          }}>
            <div style={{
              width: `${progress}%`,
              height: '100%',
              background: '#4F46E5',
              transition: 'width 0.3s',
            }} />
          </div>
        </>
      )}

      {state === 'ready' && (
        <>
          <span>Update ready</span>
          <button
            onClick={() => window.electronAPI.installUpdate()}
            style={{
              background: '#22c55e',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              padding: '6px 12px',
              cursor: 'pointer',
              fontSize: 13,
              whiteSpace: 'nowrap',
            }}
          >
            Restart Now
          </button>
          <button
            onClick={() => setState('hidden')}
            style={{
              background: 'transparent',
              color: '#94a3b8',
              border: 'none',
              cursor: 'pointer',
              fontSize: 16,
              padding: '0 4px',
            }}
          >
            &times;
          </button>
        </>
      )}
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(toast, document.body) : toast;
}
